"use client";

import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Drawer, DrawerContent, DrawerTitle } from "@/components/ui/drawer";
import { Check, X } from "lucide-react";
import { useMedia } from "react-use";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { cn } from "@/lib/utils";
import { useTranslations } from "next-intl";

export type SortOption = "price_low" | "price_high" | "newest";

interface SortDialogProps {
  open: boolean;
  onClose: () => void;
  /** null = no explicit sort chosen (list keeps its server/base order) */
  sortBy: SortOption | null;
  onSortChange: (sort: SortOption) => void;
}

export default function SortDialog({
  open,
  onClose,
  sortBy,
  onSortChange,
}: SortDialogProps) {
  const isDesktop = useMedia("(min-width: 768px)", true);
  const t = useTranslations("search.sort");

  const options: Array<{ value: SortOption; labelKey: string }> = [
    { value: "price_low", labelKey: "priceLow" },
    { value: "price_high", labelKey: "priceHigh" },
    { value: "newest", labelKey: "newest" },
  ];

  const handleSelect = (value: SortOption) => {
    onSortChange(value);
    onClose();
  };

  const Header = () => (
    <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
      <h2 className="text-base font-semibold text-gray-900">{t("title")}</h2>
      <button
        onClick={onClose}
        className="p-1.5 hover:bg-gray-100 rounded-full transition"
      >
        <X className="w-5 h-5 text-gray-400" />
      </button>
    </div>
  );

  const OptionList = () => (
    <div className="py-1">
      {options.map((option) => {
        const isSelected = sortBy === option.value;
        return (
          <button
            key={option.value}
            onClick={() => handleSelect(option.value)}
            className={cn(
              "w-full flex items-center justify-between px-4 py-3.5 text-left transition-colors",
              isSelected
                ? "text-gray-900 font-medium"
                : "text-gray-600 hover:bg-gray-50",
            )}
          >
            <span className="text-sm">{t(option.labelKey)}</span>
            {isSelected && <Check className="w-5 h-5 text-gray-900" />}
          </button>
        );
      })}
    </div>
  );

  if (isDesktop) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent
          className="max-w-sm p-0 bg-white flex flex-col gap-0 overflow-hidden rounded-2xl"
          showCloseButton={false}
        >
          <VisuallyHidden>
            <DialogTitle>{t("title")}</DialogTitle>
          </VisuallyHidden>
          <Header />
          <OptionList />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Drawer open={open} onOpenChange={onClose}>
      <DrawerContent className="p-0 bg-white h-auto max-h-none">
        <VisuallyHidden>
          <DrawerTitle>{t("title")}</DrawerTitle>
        </VisuallyHidden>
        <div className="flex flex-col gap-0">
          <div className="px-4 py-3 border-b border-gray-100 text-center">
            <h2 className="text-base font-semibold text-gray-900">{t("title")}</h2>
          </div>
          <OptionList />
        </div>
      </DrawerContent>
    </Drawer>
  );
}
